import React, { useEffect, useRef, useState, useCallback, useId } from 'react';
import DOMPurify from 'dompurify';
import { Copy, Check, Maximize2, Minimize2, Download } from 'lucide-react';
import { svgToPngBlob, copyPngToClipboard } from './diagramExportUtils';
import { triggerBlobDownload } from '@/utils/blobDownload';

let mermaidInitialized = false;

/**
 * Lazily imports and initializes Mermaid once. Subsequent calls return the cached module.
 * We defer import to avoid blocking the main bundle and use a deterministic theme
 * that blends with the slate-based chat UI.
 */
async function getMermaid() {
  const m = await import('mermaid');
  if (!mermaidInitialized) {
    m.default.initialize({
      startOnLoad: false,
      theme: 'base',
      themeVariables: {
        primaryColor: '#e2e8f0',
        primaryTextColor: '#334155',
        primaryBorderColor: '#94a3b8',
        lineColor: '#64748b',
        secondaryColor: '#f1f5f9',
        tertiaryColor: '#f8fafc',
        fontFamily: 'inherit',
        fontSize: '13px',
      },
      flowchart: { curve: 'basis', padding: 16 },
      sequence: { mirrorActors: false },
    });
    mermaidInitialized = true;
  }
  return m.default;
}

interface MermaidDiagramProps {
  code: string;
}

/**
 * Renders a Mermaid diagram from source code.
 * Styled to match CopyableTable — slate borders, copy button, and a fullscreen toggle.
 */
export const MermaidDiagram: React.FC<MermaidDiagramProps> = ({ code }) => {
  const uniqueId = useId().replace(/:/g, '-');
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const mermaid = await getMermaid();
        const { svg: rendered } = await mermaid.render(`mermaid-${uniqueId}`, code.trim());
        if (!cancelled) {
          setSvg(rendered);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Erreur de rendu du schéma');
          setSvg(null);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [code, uniqueId]);

  const handleCopy = useCallback(async () => {
    if (!svg) return;
    try {
      const blob = await svgToPngBlob(svg);
      await copyPngToClipboard(blob, code);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      try {
        await navigator.clipboard.writeText(code);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      } catch (inner) {
        console.error('[MermaidDiagram] copy failed:', inner);
      }
    }
  }, [svg, code]);

  const handleDownload = useCallback(async () => {
    if (!svg) return;
    try {
      const blob = await svgToPngBlob(svg);
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
      triggerBlobDownload(blob, `schema-${timestamp}.png`);
    } catch (e) {
      console.error('[MermaidDiagram] download failed:', e);
    }
  }, [svg]);

  if (error) {
    return (
      <div className="my-3 rounded-lg border border-red-200 bg-red-50/50 p-3">
        <p className="text-xs font-medium text-red-600 mb-1">Erreur dans le schéma</p>
        <pre className="text-xs text-red-500 whitespace-pre-wrap font-mono">{error}</pre>
        <pre className="mt-2 text-xs text-grey-600 whitespace-pre-wrap font-mono bg-white/50 rounded p-2 border border-red-100">{code}</pre>
      </div>
    );
  }

  if (!svg) {
    return (
      <div className="my-3 rounded-lg border border-grey-200 bg-grey-50/50 p-6 flex items-center justify-center">
        <div className="flex items-center gap-2 text-xs text-grey-400">
          <div className="h-3 w-3 border-2 border-grey-200 border-t-transparent rounded-full animate-spin" />
          <span>Rendu du schéma…</span>
        </div>
      </div>
    );
  }

  const diagramContent = (
    <div
      ref={containerRef}
      className={`
        ${isExpanded ? 'p-6' : 'p-4'}
        flex items-center justify-center
        [&_svg]:max-w-full [&_svg]:h-auto
      `}
      // DOMPurify: mermaid.render() produit du SVG à partir de code LLM —
      // sanitize en défense-en-profondeur contre d'éventuelles vulns mermaid (cf. CVE-2023-45809)
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(svg, { USE_PROFILES: { svg: true }, ADD_TAGS: ['foreignObject'] }) }}
    />
  );

  const actionButtons = (compact = false) => (
    <>
      {!compact && (
        <button
          type="button"
          onClick={() => setIsExpanded(true)}
          className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border bg-white border-grey-200 text-grey-600 hover:bg-grey-100 hover:text-grey-800 transition-all duration-200"
        >
          <Maximize2 className="h-3 w-3" />
          <span>Agrandir</span>
        </button>
      )}
      <button
        type="button"
        onClick={handleDownload}
        className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border bg-white border-grey-200 text-grey-600 hover:bg-grey-100 hover:text-grey-800 transition-all duration-200"
      >
        <Download className="h-3 w-3" />
        <span>Télécharger</span>
      </button>
      <button
        type="button"
        onClick={handleCopy}
        className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border transition-all duration-200
          ${isCopied
            ? 'bg-grey-100 border-grey-200 text-grey-800'
            : 'bg-white border-grey-200 text-grey-600 hover:bg-grey-100 hover:text-grey-800'
          }`}
      >
        {isCopied ? <><Check className="h-3 w-3" /><span>Copié !</span></> : <><Copy className="h-3 w-3" /><span>Copier</span></>}
      </button>
    </>
  );

  if (isExpanded) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="relative w-[90vw] max-w-5xl max-h-[85vh] rounded-xl border border-grey-200 bg-white shadow-2xl flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-grey-100 bg-grey-50/80">
            <span className="text-xs font-semibold text-grey-600 uppercase tracking-wider">Schéma</span>
            <div className="flex items-center gap-1.5">
              {actionButtons(true)}
              <button
                type="button"
                onClick={() => setIsExpanded(false)}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border bg-white border-grey-200 text-grey-600 hover:bg-grey-100 hover:text-grey-800 transition-all duration-200"
              >
                <Minimize2 className="h-3 w-3" />
                <span>Réduire</span>
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto">
            {diagramContent}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="my-3 rounded-lg border border-grey-200 bg-white overflow-hidden">
      <div className="overflow-x-auto">
        {diagramContent}
      </div>
      <div className="flex justify-end gap-1.5 px-3 py-1.5 border-t border-grey-100 bg-grey-50/50">
        {actionButtons()}
      </div>
    </div>
  );
};
